import { Option } from "@shared/types";
import { Input } from "@shared/components/shadcn/input";
import { useState, useEffect, useRef, useCallback, memo } from "react";

type Props = {
  option: Option;
  isSelected: boolean;
  onChange: (value: string) => void;
  index: number;
  name: string;
  required?: boolean;
  onEnterPress?: () => void;
  currentValue?: string; // deprecated in sentinel mode
  customValue?: string; // external custom input value (sentinel mode)
  onCustomChange?: (value: string) => void; // external updater for custom input
  sentinelOtherValue?: string; // value used to mark Other selection
};

const RadioOption = memo(function RadioOption({ option: { value, allowCustomInput, customInputPlaceholder }, required, name, isSelected, onChange, index, onEnterPress, currentValue, customValue: externalCustomValue, onCustomChange, sentinelOtherValue }: Props) {
  const [customValue, setCustomValue] = useState(externalCustomValue || "");
  const inputRef = useRef<HTMLInputElement>(null);
  
  // Focus the input when it becomes visible
  useEffect(() => {
    if (isSelected && allowCustomInput && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
    }
  }, [isSelected, allowCustomInput]);

  // Sync internal state when external custom value changes
  useEffect(() => {
    if (allowCustomInput) {
      setCustomValue(externalCustomValue || "");
    }
  }, [allowCustomInput, externalCustomValue]);

  
  const handleRadioChange = (radioValue: string) => {
    if (allowCustomInput) {
      // Select sentinel for Other; leave custom value handled separately
      setCustomValue(externalCustomValue || "");
      onChange(sentinelOtherValue || value);
    } else {
      // Regular option, just pass the value
      onChange(radioValue);
    }
  };

  const handleCustomInputChange = (inputValue: string) => {
    setCustomValue(inputValue);
    if (onCustomChange) {
      onCustomChange(inputValue);
    } else {
      // fallback: update main value directly
      onChange(inputValue.trim() || value);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && onEnterPress && customValue.trim()) {
      e.preventDefault();
      onEnterPress();
    }
  };

  const letters = "ABCDEFGHIJ";
  
  return (
    <div className="flex flex-col gap-2">
      <label
        htmlFor={name + value}
        className={`relative bg-white h-12 sm:h-10 px-3 sm:px-2.5 pr-5 cursor-pointer flex items-center gap-2.5 rounded-lg border-2 transition-all duration-200 ${
          isSelected ? "border-[#2A6354] text-[#2A6354] shadow-md" : "border-[#BFBCC0]/50 hover:border-[#BFBCC0] hover:shadow-sm"
        }`}
      >
        <div
          className={`h-6 w-6 sm:h-5 sm:w-5 min-w-[1.5rem] sm:min-w-5 rounded-md text-sm leading-none text-white grid place-content-center transition-colors duration-200 ${
            isSelected ? "bg-[#2A6354]" : "bg-[#BFBCC0]"
          }`}
        >
          {letters.split("")[index]}
        </div>
        
        {/* Always show the original value text */}
        <span className="select-none min-w-max text-base sm:text-sm">{value}</span>
        
        <input
          className="absolute inset-0 appearance-none opacity-0 w-full h-full cursor-pointer"
          type="radio"
          checked={isSelected}
          onChange={(e) => handleRadioChange(e.target.value)}
          name={name}
          value={value}
          required={required}
          id={name + value}
        />
      </label>
      
      {/* Show input field below only when this specific option is selected */}
      {allowCustomInput && isSelected && (
        <Input
          ref={inputRef}
          value={customValue}
          onChange={(e) => handleCustomInputChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={customInputPlaceholder || "Please specify..."}
          className="text-base sm:text-sm bg-[#D2CED4]/10 border-2 border-[#BFBCC0]/50 focus:border-[#2A6354] focus:ring-2 focus:ring-[#2A6354]/20 transition-all duration-200 px-4 py-3 sm:py-2 w-full"
        />
      )}
    </div>
  );
});

export default RadioOption;
